<!--
  SplitShadesDialog -- which garment colors the existing decoration goes on
  when a placement is split into light and dark routes.

  Jake, 2026-10-21: "pop a dialog asking which colors (light/dark) the
  existing decoration should be applied to, and display the decoration in
  the dialog so the user can see it to make a decision. The default should
  be based on the colors in the decoration, similar to what we do when
  choosing a background color to show behind decorations ... If the
  placement is an embroidery decoration with colorways flagged to be
  applied to light/dark garments, use the same decoration for both light and
  dark, but with the matching colorway."

  THE DEFAULT REUSES THE BACKDROP DECISION, it does not copy it. The preview
  is a DecorationArtThumb, the one component that decides which backdrop
  decoration art is visible on (utils/artBackdrop.js: server-sampled ink,
  then a CORS-safe sample of the loaded image, then unknown). Its
  `resolved` key IS the suggestion:

    light backdrop (dark ink)       -> light garments
    dark backdrop (light ink)       -> dark garments
    neutral (two-tone, or unknown)  -> both

  Embroidery whose colorways are flagged per shade overrides that: both
  shades, each sewing its matching colorway (the host binds them after the
  split and passes their names in for display).

  The suggestion is applied until the user touches a checkbox, never after:
  a late image sample must not silently undo a deliberate choice.

  Presentational only: the host owns the write. Exactly one outcome per
  interaction -- the same closingFromAction guard as ReplaceScopeDialog,
  because Vuetify echoes our own close back as update:model-value=false and
  that echo must not read as a cancel.
-->
<template>
    <v-dialog
        :model-value="modelValue"
        max-width="460"
        @update:model-value="onUpdate"
    >
        <v-card data-testid="split-shades-dialog">
            <v-card-title>Split light / dark</v-card-title>
            <v-card-text>
                <div class="ssd-art">
                    <DecorationArtThumb
                        v-if="thumbnailUrl"
                        :src="thumbnailUrl"
                        :ink-hex="inkHex"
                        :ink-mixed="inkMixed"
                        :alt="`${decorationName} preview`"
                        class="ssd-thumb"
                        data-testid="split-shades-dialog-preview"
                        @resolved="onBackdropResolved"
                    />
                    <div
                        v-else
                        class="ssd-thumb ssd-thumb-empty"
                        data-testid="split-shades-dialog-no-preview"
                    >
                        No preview
                    </div>
                    <div class="ssd-art-name">
                        {{ decorationName }}
                    </div>
                </div>
                <p class="ssd-lede">
                    Which garment colors should this decoration go on?
                </p>
                <!--
                  Native checkboxes, not v-checkbox: Vuetify routes data-*
                  attributes to v-checkbox's ROOT div, so the testid would
                  name a wrapper a click can miss, not the control.
                -->
                <label class="ssd-option">
                    <input
                        v-model="light"
                        type="checkbox"
                        data-testid="split-shades-dialog-light"
                        @change="touched = true"
                    >
                    <span>
                        Light garments
                        <span
                            v-if="shadeColorwayNames.light"
                            class="ssd-colorway"
                            data-testid="split-shades-dialog-light-colorway"
                        >Thread colors: {{ shadeColorwayNames.light }}</span>
                    </span>
                </label>
                <label class="ssd-option">
                    <input
                        v-model="dark"
                        type="checkbox"
                        data-testid="split-shades-dialog-dark"
                        @change="touched = true"
                    >
                    <span>
                        Dark garments
                        <span
                            v-if="shadeColorwayNames.dark"
                            class="ssd-colorway"
                            data-testid="split-shades-dialog-dark-colorway"
                        >Thread colors: {{ shadeColorwayNames.dark }}</span>
                    </span>
                </label>
                <p
                    class="ssd-muted"
                    data-testid="split-shades-dialog-suggestion"
                    :data-suggested="suggestedKey"
                >
                    {{ suggestionText }}
                    A color left unchecked gets its own empty card, ready
                    for a different decoration.
                </p>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    variant="text"
                    data-testid="split-shades-dialog-cancel"
                    @click="choose('cancel')"
                >
                    Cancel
                </v-btn>
                <v-spacer />
                <v-btn
                    color="primary"
                    variant="flat"
                    :disabled="!canConfirm"
                    data-testid="split-shades-dialog-confirm"
                    @click="onConfirm"
                >
                    Split
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import DecorationArtThumb from "@/components/decoration/DecorationArtThumb.vue";
import { resolveServerArtBackdrop } from "@/utils/artBackdrop";

const props = defineProps({
    modelValue: { type: Boolean, required: true },
    decorationName: { type: String, default: "This decoration" },
    /** Preview image of the decoration; null when none is ready. */
    thumbnailUrl: { type: String, default: null },
    /** ``Artwork.dominant_ink_hex`` -- raw, null when unknown. */
    inkHex: { type: String, default: null },
    /** ``Artwork.dominant_ink_mixed`` (two-tone art). */
    inkMixed: { type: Boolean, default: false },
    /** Embroidery whose colorways are flagged for light / dark garments. */
    shadesDeclared: { type: Boolean, default: false },
    /** Display only: the colorway each shade will sew, when known. */
    shadeColorwayNames: {
        type: Object,
        default: () => ({ light: null, dark: null }),
    },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

// The backdrop the decoration's ink calls for. It STARTS from the server's
// answer -- the same first rung DecorationArtThumb applies -- so the boxes
// are right on the first frame, and the thumb's `resolved` event refines it
// (a client-side sample when the server had none).
//
// The event is applied on the NEXT tick, deliberately. The thumb emits while
// it mounts, which is in the middle of the dialog overlay's own update; a
// change made then was observed on 8086 to update the hint text while the
// checkboxes kept showing the old default. Deferring makes it a normal
// update of its own.
const backdropKey = ref(
    resolveServerArtBackdrop(props.inkHex, props.inkMixed) ?? "neutral",
);
function onBackdropResolved(key) {
    nextTick(() => {
        backdropKey.value = key;
    });
}

const suggestedKey = computed(() => {
    if (props.shadesDeclared) return "both";
    if (backdropKey.value === "light") return "light";
    if (backdropKey.value === "dark") return "dark";
    return "both";
});

const suggestionText = computed(() => {
    if (props.shadesDeclared) {
        return "Its colorways are set up for light and dark garments, so both "
            + "are suggested, each with its matching thread colors.";
    }
    if (suggestedKey.value === "light") {
        return "Suggested from its colors: it reads best on light garments.";
    }
    if (suggestedKey.value === "dark") {
        return "Suggested from its colors: it reads best on dark garments.";
    }
    return "Suggested: both.";
});

const light = ref(true);
const dark = ref(true);
const touched = ref(false);

watch(
    suggestedKey,
    (key) => {
        if (touched.value) return;
        light.value = key !== "dark";
        dark.value = key !== "light";
    },
    // Synchronous, so the boxes never show a stale suggestion for a frame.
    { immediate: true, flush: "sync" },
);

const canConfirm = computed(() => light.value || dark.value);

// See the header: our own close echoes back through onUpdate.
let closingFromAction = false;

function onUpdate(v) {
    emit("update:modelValue", v);
    if (v) return;
    if (closingFromAction) {
        closingFromAction = false;
        return;
    }
    emit("cancel");
}

function choose(outcome, payload) {
    closingFromAction = true;
    emit("update:modelValue", false);
    if (payload === undefined) emit(outcome);
    else emit(outcome, payload);
}

function onConfirm() {
    if (!canConfirm.value) return;
    const shades = [];
    if (light.value) shades.push("light");
    if (dark.value) shades.push("dark");
    choose("confirm", shades);
}
</script>

<style scoped>
.ssd-art {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 12px;
}
.ssd-thumb {
    width: 96px;
    height: 96px;
    object-fit: contain;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    flex: 0 0 auto;
}
.ssd-thumb-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
}
.ssd-art-name {
    font-weight: 600;
    overflow-wrap: anywhere;
}
.ssd-lede {
    margin-bottom: 8px;
}
.ssd-option {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    padding: 4px 0;
    cursor: pointer;
}
.ssd-option input {
    margin-top: 4px;
}
.ssd-colorway {
    display: block;
    font-size: 0.8125rem;
    color: rgba(0, 0, 0, 0.6);
}
.ssd-muted {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.875rem;
    margin-top: 8px;
}
</style>
